import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import { getFirestore, doc, getDoc } from 'firebase/firestore'; 
import SharePuzzle from './SharePuzzle';


const cream = '#F5EEDD';
const palettes = ['#E63946', '#F4A259', '#2A9D8F', '#457B9D', '#E9C46A'];

const styles = {
  page: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: '100vh',
    padding: '90px 16px 16px',
    fontFamily: 'Helvetica, Arial, sans-serif',
    background: '#FAF9F7',
    overflow: 'hidden'
  },
  title: { fontSize: '1.5rem', fontWeight: 700, marginTop: 16, marginBottom: 8 },
  subtitle: { fontSize: '1rem', marginBottom: 20, color: '#333' },
  clueButton: {
    marginBottom: 20,
    padding: '8px 16px',
    fontSize: '1rem',
    borderRadius: 6,
    border: 'none',
    backgroundColor: '#2563EB',
    color: 'white',
    cursor: 'pointer'
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 },
  blockOuter: (c, sel, correct) => ({
    width: 110,
    height: 110,
    background: c,
    borderRadius: 12,
    boxShadow: correct
      ? '0 0 0 6px #00CC66, 0 4px 8px rgba(0,0,0,0.25)'
      : sel
      ? '0 0 0 6px #FFDD00, 0 4px 8px rgba(0,0,0,0.25)'
      : '0 4px 8px rgba(0,0,0,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'box-shadow .15s'
  }),
  blockInner: (c) => ({
    position: 'relative',
    width: '82%',
    height: '82%',
    background: cream,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '"Times New Roman", serif',
    fontSize: '5rem',
    fontWeight: 700,
    color: c
  }),
  tooltip: {
    position: 'absolute',
    bottom: '120%',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#333',
    color: '#fff',
    borderRadius: 6,
    padding: '4px 8px',
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
    opacity: 0,
    transition: 'opacity 0.2s',
    pointerEvents: 'none'
  },
  tooltipVisible: { opacity: 1 },
  card: {
    marginTop: 32,
    padding: 24,
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 6px 18px rgba(0,0,0,.25)',
    maxWidth: 480,
    textAlign: 'center'
  },
  quote: { color: '#2563EB', fontSize: '0.875rem', marginTop: 16 },
  coinAnimation: {
    position: 'absolute',
    top: 20,
    right: 20,
    fontSize: '2rem',
    animation: 'fall 1s ease-out'
  },

 graffitiBanner: {
  position: 'absolute',
  top: '50%',
  /* left: '50%', */
  transform: 'translate(-50%, -50%)',
  fontSize: '4rem',
  fontWeight: 600,
  color: '#ff3cac',
  textShadow: '2px 2px 5px #000',
  zIndex: 9999,
  pointerEvents: 'none',
  textAlign: 'center'
  }, 

wrapper: {
  position: 'relative',
  display: 'inline-block',
  margin: '0 auto'
}

};

export default function DailyWordPuzzle() {
  const [tiles, setTiles] = useState([]);
  const [word, setWord] = useState('');
  const [definition, setDefinition] = useState('');
  const [sentence, setSentence] = useState('');
  const [quote, setQuote] = useState('');
  const [isSolved, setIsSolved] = useState(false);
  const [showGraffiti, setShowGraffiti] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [coins, setCoins] = useState(() => parseInt(localStorage.getItem('coins')) || 500);
  const [showCoin, setShowCoin] = useState(false);

  const coinSound = useRef(null);
  const clueSound = useRef(null);

  useEffect(() => {
    const handleUserInteraction = () => {
      document.removeEventListener('click', handleUserInteraction);
      coinSound.current?.play().catch(() => {});
      triggerCoinAnimation();
    };
    document.addEventListener('click', handleUserInteraction);
  }, []);

  useEffect(() => {
    localStorage.setItem('coins', coins);
    window.dispatchEvent(new Event('storage'));
  }, [coins]);

  useEffect(() => {
    const fetchWordFromFirestore = async () => {
      const today = new Date().toISOString().slice(0, 10);
      const db = getFirestore();
      try {
        const docSnap = await getDoc(doc(db, 'wordSchedule', today));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setWord(data.word);
          setTiles(data.shuffled.split(''));
          setDefinition(data.definition);
          setSentence(data.sentence);
          setQuote(data.quote);
          coinSound.current?.play();
          triggerCoinAnimation();
        } else {
          const fallback = { word: 'FLOWCHART', shuffled: 'CWHLFTRAO' };
          setWord(fallback.word);
          setTiles(fallback.shuffled.split(''));
        }
      } catch (err) {
        console.error("🔥 Failed to fetch word from Firestore:", err);
      }
    };
    fetchWordFromFirestore();
  }, []);

  useEffect(() => {
    if (word && tiles.join('') === word) {
      setIsSolved(true);
      setCoins((prev) => prev + 20);
      setShowGraffiti(true);
      setTimeout(() => setShowGraffiti(false), 3000);
    }
  }, [tiles, word]);

  const handleBlockClick = (idx) => {
    if (selectedIdx === null) return setSelectedIdx(idx);
    if (selectedIdx === idx) return setSelectedIdx(null);
    const newTiles = [...tiles];
    [newTiles[selectedIdx], newTiles[idx]] = [newTiles[idx], newTiles[selectedIdx]];
    setTiles(newTiles);
    setSelectedIdx(null);
    setCoins((p) => Math.max(p - 5, 0));
  };

  const handleGetClue = () => {
    if (coins < 10) return alert('Not enough coins for a clue.');
    for (let i = 0; i < word.length; i++) {
      if (tiles[i] !== word[i]) {
        const correctChar = word[i];
        const targetIdx = tiles.indexOf(correctChar, i);
        if (targetIdx !== -1) {
          const newTiles = [...tiles];
          [newTiles[i], newTiles[targetIdx]] = [newTiles[targetIdx], newTiles[i]];
          setTiles(newTiles);
          setCoins((p) => p - 10);
          clueSound.current?.play();
        }
        break;
      }
    }
  };

  const triggerCoinAnimation = () => {
    setShowCoin(true);
    setTimeout(() => setShowCoin(false), 1000);
  };

  return (
    <main style={styles.page}>
      <h1 style={styles.title}>The Daily Word Puzzle</h1>
      <div style={styles.subtitle}>Swap the letters to find the Word of the Day!</div>
      <button style={styles.clueButton} onClick={handleGetClue}>Get a Clue (-10 coins)</button>
      
      <div style={styles.wrapper}>
      <div style={styles.grid}>
        {tiles.map((ch, i) => {
          const colour = palettes[i % palettes.length];
          const isCorrect = word && ch === word[i];
          const showTip = hoverIdx === i && isCorrect;
          return (
            <div
              key={i}
              style={styles.blockOuter(colour, selectedIdx === i, isCorrect)}
              onClick={() => handleBlockClick(i)}
              onMouseEnter={() => isCorrect && setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
            >
              <div style={styles.blockInner(colour)}>
                {ch}
                {showTip && (
                  <span style={{
                    ...styles.tooltip,
                    ...(showTip ? styles.tooltipVisible : {})
                  }}>
                    This piece is in the correct location!
                  </span>
                )}
              </div>
            </div>
          );
        })}
  </div>
      <AnimatePresence>
    {showGraffiti && (
      <motion.div
        style={styles.graffitiBanner}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1.1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ duration: 1.5 }}
      >
        🎉 You Rock! 🎉
      </motion.div>
    )}
  </AnimatePresence>
</div>

      {isSolved && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          style={styles.card}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Congratulations!</h2>
          <p style={{ marginTop: 8 }}>
            <strong>{word}</strong>: {definition}
          </p>
          <p style={{ fontStyle: 'italic', marginTop: 8 }}>&quot;{sentence}&quot;</p>
          <p style={styles.quote}>{quote}</p>
          <SharePuzzle puzzleUrl={`https://thatpuzzleapp.com/thedailywordpuzzle`} />
        </motion.div>
      )}



{isSolved && <Confetti numberOfPieces={300} recycle={false} />}

      {showCoin && (
        <motion.div
          style={styles.coinAnimation}
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          💰
        </motion.div>
      )}

      <audio ref={coinSound} src="/sounds/coin-pour.mp3" preload="auto" />
      <audio ref={clueSound} src="/sounds/register.mp3" preload="auto" />

    </main>
  );
}
